// import flashDealsBannerAvif from '@/assets/images/flash-deals-banner.avif'
// import flashDealsBannerPng from '@/assets/images/flash-deals-banner.png?lqip'
// import flashDealsBannerMobileAvif from '@/assets/images/flash-deals-banner-mobile.avif'
// import flashDealsBannerMobilePng from '@/assets/images/flash-deals-banner-mobile.png?lqip'
import {useHarmonicIntervalFn} from 'react-use'

import flashDealsBannerAvif from '@/assets/images/christmas-sales-banner.avif'
import flashDealsBannerPng from '@/assets/images/christmas-sales-banner.png?lqip'
import flashDealsBannerMobileAvif from '@/assets/images/christmas-sales-banner-mobile.avif'
import flashDealsBannerMobilePng from '@/assets/images/christmas-sales-banner-mobile.png?lqip'
import ProductCard from '@/components/ProductCard/ProductCard'
import SkipLink from '@/components/SkipLink'
import useNumberOfItemsPerRow from '@/hooks/useNumberOfItemsPerRow'
import Footer from '@/layout/Footer'
import Navbar from '@/layout/Navbar'
import HeadTags from '@/lib/head/HeadTags'
import skipTargetProps from '@/utils/a11y/skipTargetProps'
import VisuallyHidden from '@/utils/a11y/VisuallyHidden'
import {useFlashDeals} from '@/views/Home/components/HomePageFlashDeals/HomePageFlashDeals'

import style from './FlashDeals.module.scss'

const TARGET_DATE_STRING = 'December 20, 2024, at 12 AM (GST)'
export const FLASH_DEALS_TARGET_DATE = new Date('2024-12-20T00:00:00+04:00') // GST is UTC+4
// export const FLASH_DEALS_TARGET_DATE = new Date(0)

const TITLE = 'Christmas Sales'
const BANNER_DESCRIPTION = 'Enjoy up to 90% off'

function Timer1() {
  // eslint-disable-next-line @eslint-react/naming-convention/use-state -- it's intentional
  const [, setResetController] = useState(0)

  const reset = useCallback(() => {
    setResetController(resetState => resetState + 1)
  }, [])

  const now = new Date()
  const secondsUntilTarget = Math.floor((FLASH_DEALS_TARGET_DATE.getTime() - now.getTime()) / 1000)

  const days = Math.floor(secondsUntilTarget / 86400)
  const hours = Math.floor((secondsUntilTarget % 86400) / 3600)
  const minutes = Math.floor((secondsUntilTarget % 3600) / 60)
  const seconds = Math.floor(secondsUntilTarget % 60)

  useHarmonicIntervalFn(() => {
    reset()
  }, 1000)

  return (
    <div className={style.Timer1}>
      <VisuallyHidden as='br' />
      Sale starts in{' '}
      <div className={style.Timer1Time}>
        <div
          className={style.Timer1Sup}
        >{`${days} day${days > 1 ? 's' : ''}, ${hours} hour${hours > 1 ? 's' : ''}, ${minutes} minute${minutes > 1 ? 's' : ''} and ${seconds} second${seconds > 1 ? 's' : ''}`}</div>
        <div className={style.Timer1Sub}>{TARGET_DATE_STRING}</div>
      </div>
    </div>
  )
}

interface Props {
  remainingSeconds: number
  label: string
}

function Timer(props: Props) {
  const [remainingSeconds, setRemainingSeconds] = useState(props.remainingSeconds)

  useHarmonicIntervalFn(() => {
    setRemainingSeconds(prev => prev - 1)
  }, 1000)

  const hours = Math.floor(remainingSeconds / 3600)
  const minutes = Math.floor((remainingSeconds % 3600) / 60)
  const seconds = Math.floor(remainingSeconds % 60)

  return (
    <>
      <VisuallyHidden>
        {hours} hours, {minutes} minutes, {seconds} seconds remaining until the {props.label} end
      </VisuallyHidden>
      <div className={style.FlashDealsTimer} aria-hidden>
        <div aria-hidden>
          <div className={style.TextSup} aria-hidden>
            {hours}
          </div>
          <div className={style.TextSub} aria-hidden>
            Hour{hours > 1 ? 's' : ''}
          </div>
        </div>
        <div aria-hidden>
          <div className={style.TextSup} aria-hidden>
            {minutes}
          </div>
          <div className={style.TextSub} aria-hidden>
            Minute{minutes > 1 ? 's' : ''}
          </div>
        </div>
        <div aria-hidden>
          <div className={style.TextSup} aria-hidden>
            {seconds}
          </div>
          <div className={style.TextSub} aria-hidden>
            Second{seconds > 1 ? 's' : ''}
          </div>
        </div>
      </div>
    </>
  )
}

export default memo(function FlashDeals() {
  const {data: flashDeals} = useFlashDeals()

  const numberOfItemsPerRow = useNumberOfItemsPerRow()

  const productWith = `${100 / numberOfItemsPerRow}%`

  const imgAvif = numberOfItemsPerRow >= 3 ? flashDealsBannerAvif : flashDealsBannerMobileAvif
  const imgPng = numberOfItemsPerRow >= 3 ? flashDealsBannerPng : flashDealsBannerMobilePng

  const timerPassed = new Date() >= FLASH_DEALS_TARGET_DATE

  return (
    <>
      <div className={clsx(style.Page, timerPassed && style.ShouldHaveBackground)}>
        <HeadTags title={TITLE} />
        <SkipLink title='Skip to main content' to='#main-content' />
        <Navbar variant='dark' withSearch displayNavBanner />
        <main {...skipTargetProps('main-content')}>
          <div className={style.Container}>
            <VisuallyHidden as='h1'>Innosearch AI's {TITLE}</VisuallyHidden>
            {/* <a href='https://blog.innosearch.ai/innosearch-savings-promotion'> */}
            <picture>
              <source srcSet={imgAvif} type='image/avif' />
              <img
                fetchPriority='high'
                src={imgPng.src}
                alt=''
                className={style.PromotionBanner}
                style={{backgroundImage: `url("${imgPng.lqip}")`, backgroundSize: 'cover'}}
              />
            </picture>
            <VisuallyHidden>
              {TITLE}: {BANNER_DESCRIPTION}
            </VisuallyHidden>
            {/* </a> */}
            {!timerPassed && <Timer1 />}
            {timerPassed && (
              <>
                {flashDeals && (
                  <div className={style.TimerContainer}>
                    <Timer
                      key={flashDeals.remainingSeconds}
                      remainingSeconds={flashDeals.remainingSeconds}
                      label={TITLE}
                    />
                  </div>
                )}
                <div className={style.Products}>
                  {/* TODO: virtualization */}
                  {flashDeals &&
                    flashDeals.products.map(product => (
                      <div
                        className={style.Product}
                        style={{
                          width: productWith,
                        }}
                        key={`${product.retailer}-${product.id}`}
                      >
                        <ProductCard product={product} headingLevel={3} />
                      </div>
                    ))}
                </div>
              </>
            )}
          </div>
        </main>
      </div>
      <Footer />
    </>
  )
})
